/**
 * ContinueButton — single canonical primary CTA for the V7 onboarding funnel.
 *
 * Why this exists:
 *   Every onboarding step had its own Continue button styling — pill vs
 *   rounded-lg, px-7 vs px-8 vs px-9, different shadow stacks, different
 *   hover treatments. Mason flagged the inconsistency. This component is
 *   the single source of truth so every step looks identical.
 *
 * Design (matches the synthesis variant style + homepage hover pattern):
 *   - Pill (rounded-full)
 *   - px-9 py-3.5 · text-[15px] · font-semibold (Inter, default sans)
 *   - Gold bg, dark-gold text, inset highlight + drop shadow
 *   - Trailing arrow that nudges right on hover
 *   - Hover: lift 1px + brightness +4% + shadow expand with gold glow
 *   - Disabled: surface-high bg, muted text, no shadow, not-allowed cursor
 *   - Focus-visible: 2px gold ring with surface offset
 *
 * Usage:
 *   <ContinueButton onClick={onContinue}>Continue</ContinueButton>
 *   <ContinueButton onClick={onSkip} disabled>Skip for now</ContinueButton>
 *
 * Custom label is supported. Default = "Continue" with trailing arrow.
 */

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

export default function ContinueButton({
  onClick,
  disabled = false,
  children = 'Continue',
  ariaLabel,
  type = 'button',
}) {
  const labelText = typeof children === 'string' ? children : 'Continue'

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || labelText}
      className="group relative inline-flex items-center justify-center gap-2.5 font-semibold text-[15px] tracking-[0.005em] px-9 py-3.5 rounded-full select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:cursor-not-allowed"
      style={{
        background: disabled ? 'var(--bg-surface-high)' : 'var(--gold)',
        color: disabled ? 'var(--text-muted)' : 'var(--text-on-gold)',
        boxShadow: disabled
          ? 'none'
          : '0 1px 0 rgba(255,255,255,0.18) inset, 0 6px 18px -6px rgba(233, 195, 73, 0.45)',
        transitionProperty: 'transform, box-shadow, filter, background, color',
        transitionDuration: '280ms, 320ms, 240ms, 220ms, 220ms',
        transitionTimingFunction: EASE,
      }}
      onMouseEnter={e => {
        if (disabled) return
        e.currentTarget.style.transform = 'translateY(-1px)'
        e.currentTarget.style.boxShadow =
          '0 1px 0 rgba(255,255,255,0.22) inset, 0 12px 28px -8px rgba(233, 195, 73, 0.55), 0 0 0 1px rgba(233, 195, 73, 0.45)'
        e.currentTarget.style.filter = 'brightness(1.04)'
      }}
      onMouseLeave={e => {
        if (disabled) return
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow =
          '0 1px 0 rgba(255,255,255,0.18) inset, 0 6px 18px -6px rgba(233, 195, 73, 0.45)'
        e.currentTarget.style.filter = 'brightness(1)'
      }}
      onMouseDown={e => {
        if (disabled) return
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.filter = 'brightness(0.98)'
      }}
      onMouseUp={e => {
        if (disabled) return
        e.currentTarget.style.transform = 'translateY(-1px)'
        e.currentTarget.style.filter = 'brightness(1.04)'
      }}
    >
      <span>{children}</span>
      <span
        aria-hidden="true"
        className="inline-block"
        style={{ transition: `transform 280ms ${EASE}` }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          className="transition-transform duration-300 ease-out group-hover:translate-x-[3px]"
          style={{ transitionTimingFunction: EASE }}
        >
          <path
            d="M3 7H11M11 7L7.5 3.5M11 7L7.5 10.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  )
}
